/**
 * THE OWNERS A SPREADSHEET NAMES WHO ARE NOT IN THE ORGANIZATION.
 *
 * Shown before the import runs and again on its report. It answers three
 * things a report full of "Owner x is not an active member" notes did not:
 * who is needed, how to get them in (invite them here, or take a file the
 * Staff page loads), and what happens to their policies meanwhile.
 *
 * Nothing here blocks the import. A policy whose owner has not joined is held
 * by whoever runs the import and passes to its owner when they do
 * (functions/src/import/pendingOwners.ts), so inviting before or after the
 * run ends in the same place.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import { GroupCard } from '../../shared/GroupCard';
import { errorText, saveText } from './ImportPage';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import field from '../admin/Admin.module.css';

type OwnerState = 'member' | 'invited' | 'not_invited' | 'unreachable';
interface OwnerRow { email: string; policies: number; state: OwnerState }
interface OwnersAnswer { people: number; policies: number; listed: number; owners: OwnerRow[] }
interface InviteAnswer { invited: number; emailFailures: number; failed: { email: string; reason: string }[]; remaining: number }

const STATE: Record<OwnerState, { label: string; pill: string }> = {
  member: { label: 'Joined', pill: own.pillActive! },
  invited: { label: 'Invited, not signed in yet', pill: own.pillDraft! },
  not_invited: { label: 'Not invited', pill: own.pillOther! },
  unreachable: { label: 'Cannot be emailed: a test address', pill: own.pillOther! },
};

const n = (v: number) => v.toLocaleString('en-US');
const people = (v: number) => `${n(v)} ${v === 1 ? 'person' : 'people'}`;
const policies = (v: number) => `${n(v)} ${v === 1 ? 'policy' : 'policies'}`;

/** How many rows are shown before "Show all". A library has dozens of owners. */
const SHOWN = 10;

export function ImportOwners({ jobId, named, ran }: {
  jobId: string;
  /** How many people validation found. Zero: the card is not shown at all. */
  named: number;
  /** The import has run, so the wording is about policies that exist. */
  ran: boolean;
}) {
  const qc = useQueryClient();
  const [arming, setArming] = useState(false);
  const [all, setAll] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['import-owners', jobId],
    enabled: named > 0,
    queryFn: () => call<{ jobId: string }, OwnersAnswer>('getImportOwners', { jobId }),
  });

  const invite = useMutation({
    // One call sends a bounded number; keep asking until none are left.
    mutationFn: async () => {
      const total = { invited: 0, emailFailures: 0, failed: [] as InviteAnswer['failed'] };
      for (let guard = 0; guard < 10; guard++) {
        const r = await call<{ jobId: string }, InviteAnswer>('inviteImportOwners', { jobId });
        total.invited += r.invited;
        total.emailFailures += r.emailFailures;
        total.failed.push(...r.failed);
        if (r.remaining === 0 || r.invited === 0) break;
      }
      return total;
    },
    onSuccess: (r) => {
      setArming(false);
      setFailure(r.failed.length ? `${people(r.failed.length)} could not be invited: ${r.failed.slice(0, 5).map((f) => f.email).join(', ')}${r.failed.length > 5 ? '…' : ''}` : null);
      setSaid(`${people(r.invited)} invited as Authors.${r.emailFailures ? ` ${n(r.emailFailures)} of the emails could not be sent; resend those from Staff.` : ''}`);
      void qc.invalidateQueries({ queryKey: ['import-owners', jobId] });
    },
    onError: (e) => { setArming(false); setFailure(errorText(e, 'The invitations could not be sent.')); },
  });

  const download = useMutation({
    mutationFn: () => call<{ jobId: string }, { fileName: string; csv: string; count: number }>('downloadImportOwnersCsv', { jobId }),
    onSuccess: (r) => saveText(r.fileName, r.csv),
    onError: (e) => setFailure(errorText(e, 'The file could not be prepared.')),
  });

  if (named === 0) return null;

  const rows = q.data?.owners ?? [];
  const waiting = rows.filter((o) => o.state !== 'member');
  const toInvite = rows.filter((o) => o.state === 'not_invited').length;
  const unreachable = rows.filter((o) => o.state === 'unreachable').length;
  const shown = all ? rows : rows.slice(0, SHOWN);

  return (
    <div style={{ margin: 'var(--sp-4) 0' }}>
      <GroupCard title="Owners who are not in this organization" meta={q.data ? people(q.data.people) : undefined}>
        {q.isLoading && <p className={page.itemMeta}>Looking up who is already here…</p>}
        {q.error && <p className={form.error} role="alert">{errorText(q.error, 'The list of owners could not be loaded.')}</p>}
        {q.data && (
          <>
            <p className={page.itemMeta} style={{ maxWidth: '80ch', margin: '0 0 var(--sp-3)' }}>
              The spreadsheet names <strong>{people(q.data.people)}</strong> as the owner of {policies(q.data.policies)} who {q.data.people === 1 ? 'is' : 'are'} not
              {' '}a member of this organization.{' '}
              {ran
                ? <>You own those policies for now. Each one passes to its owner by itself when that person joins, so nothing has to be changed by hand.</>
                : <>You can import anyway: you will own those policies, and each one passes to its owner by itself when that person joins. Inviting them now or afterwards ends in the same place.</>}
            </p>

            <table className={field.table}>
              <caption className="vh">Owners named by the spreadsheet who are not members</caption>
              <thead><tr><th scope="col">Email</th><th scope="col">Policies</th><th scope="col">Where they stand</th></tr></thead>
              <tbody>
                {shown.map((o) => (
                  <tr key={o.email}>
                    <td>{o.email}</td>
                    <td className="tnum">{n(o.policies)}</td>
                    <td><span className={`${own.pill} ${STATE[o.state].pill}`}>{STATE[o.state].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > SHOWN && (
              <p className={page.itemMeta} style={{ margin: 'var(--sp-2) 0 0' }}>
                <button type="button" className={page.linkBtn} onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${n(rows.length)}`}</button>
                {q.data.people > q.data.listed && <> · The first {n(q.data.listed)} of {n(q.data.people)} are listed.</>}
              </p>
            )}
            {unreachable > 0 && (
              <p className={page.itemMeta} style={{ maxWidth: '80ch', margin: 'var(--sp-3) 0 0' }}>
                {people(unreachable)} {unreachable === 1 ? 'has an address' : 'have addresses'} on a domain reserved for examples and tests, where no mail arrives. They are never invited from here.
              </p>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap', marginTop: 'var(--sp-4)' }}>
              {!arming ? (
                <button type="button" className={page.btn} disabled={toInvite === 0 || invite.isPending} onClick={() => { setSaid(null); setFailure(null); setArming(true); }}>
                  {toInvite === 0 ? 'Nobody left to invite' : `Invite ${people(toInvite)} as Authors…`}
                </button>
              ) : (
                <>
                  <span className={page.itemMeta}>Sends an invitation email to {people(toInvite)} now. Sure?</span>
                  <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={invite.isPending} onClick={() => invite.mutate()}>{invite.isPending ? 'Inviting…' : 'Yes, send the invitations'}</button>
                  <button type="button" className={page.btn} disabled={invite.isPending} onClick={() => setArming(false)}>Not now</button>
                </>
              )}
              <button type="button" className={page.btn} disabled={waiting.length === 0 || download.isPending} onClick={() => download.mutate()}>
                Download the list (CSV)
              </button>
            </div>
            <p className={form.hint} style={{ maxWidth: '80ch' }}>
              An owner edits, submits and revises their policies, so they are invited as Authors; change a role on Staff afterwards.
              The CSV is in the Staff page&rsquo;s own format: add names to it if you like and load it there to invite people later.
            </p>
            {said && <p className={page.itemMeta} role="status"><strong>{said}</strong></p>}
            {failure && <p className={form.error} role="alert">{failure}</p>}
          </>
        )}
      </GroupCard>
    </div>
  );
}
